import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default__default, { useState, useEffect } from 'react';
import PropTypes__default from 'prop-types';
import styles from '../../node_modules/@flixbus/honeycomb/dist/scss/components/data-table.scss.js';
import { sortDataTableRows, presetComparator } from './sorting-presets/sorting-presets.js';
import { CellContent, SelectionInputProps } from './data-table-prop-types.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import ReactUtils from '../../packages/react-utils/react-utils.js';
import DataTableHeader from './quarks/data-table-header/data-table-header.js';
import DataTableRow from './quarks/data-table-row/data-table-row.js';
import DataTableFooter from './quarks/data-table-footer/data-table-footer.js';
import { Injecss } from '../../packages/injecss/injecss.js';

const _excluded = ["caption", "headers", "rows", "footers", "plain", "small", "collapsed", "scrollable", "height", "hasSelectableRows", "onRowSelection", "sortingPropsList", "pager", "innerRef", "extraClasses"];
const dataTableControlsBem = HoneyBem.init('data-table-controls', styles);
const dataTableWrapperBem = HoneyBem.init('data-table-wrapper', styles);
const dataTableBem = HoneyBem.init('data-table', styles);
const buildRowState = row => ({
  value: row.selectionInputProps.value,
  selected: !!row.selectionInputProps.selected
});
const buildInitialSelection = (rows, hasSelectableRows) => {
  if (hasSelectableRows) {
    return rows.map(row => buildRowState(row));
  }
  return [];
};
const rebuildSelection = (rows, hasSelectableRows, rowsState) => {
  if (hasSelectableRows) {
    return rows.map(row => {
      const rowState = rowsState.find(rs => row.selectionInputProps && rs.value === row.selectionInputProps.value);
      return rowState || buildRowState(row);
    });
  }
  return [];
};
const DataTable = _ref => {
  let {
      caption,
      headers,
      rows,
      footers,
      plain,
      small,
      collapsed,
      scrollable,
      height,
      hasSelectableRows,
      onRowSelection,
      sortingPropsList,
      pager,
      innerRef,
      extraClasses
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const [rowsState, setRowsState] = useState(buildInitialSelection(rows, hasSelectableRows));
  const [sortedRows, setSortedRows] = useState(rows);

  /**
   * Listens to rows, rebuilds rowsState object when rows are updated
   * only does the update when selectable rows are turned on
   */
  useEffect(() => {
    setRowsState(rebuildSelection(rows, hasSelectableRows, rowsState));
  }, [rows, hasSelectableRows === true]);

  /** Listens to
   * - rowsState changes to call back onRowSelection with table status
   * - rows changes to update sortedRows with new props */
  useEffect(() => {
    if (onRowSelection) onRowSelection(rowsState);
    setSortedRows(rows);
  }, [rowsState, rows]);

  /**
   * Handles select-all changes on data-table-header child component
   * @param {RowState} rowState - { value: string, selected: boolean }
   */
  const toggleSelectAll = rowState => {
    const newRowsState = rowsState.map(row => ({
      value: row.value,
      selected: rowState.selected
    }));
    setRowsState(newRowsState);
  };

  /**
   * Handles row selection changes on data-table-row child component
   * @param {RowState} changedRow - { value: string, selected: boolean }
   */
  const onRowChange = changedRow => {
    const newRowsState = rowsState.map(row => row.value === changedRow.value ? changedRow : row);
    setRowsState(newRowsState);
  };

  /**
   * Returns the selection status of a given row
   * @param {string} rowName - the row value
   */
  const getRowSelectionStatus = rowName => {
    const row = rowsState.find(currentRow => currentRow.value === rowName);
    return row && row.selected;
  };

  /** Returns true if all rows are selected */
  const hasAllRowsSelected = () => rowsState.every(currentRow => currentRow.selected === true);

  /** Uses preset sorting when no custom sortingFunction is provided for a column */
  const onSortingChange = (newSorting, comparator) => {
    const newSortedRows = sortDataTableRows(sortedRows, newSorting.columnIndex, comparator || presetComparator, newSorting.ascendingOrder);
    setSortedRows(newSortedRows);
  };
  const wrapperClasses = dataTableWrapperBem.b({
    scrollable,
    collapsed
  }, extraClasses);
  const wrapperStyle = scrollable && height ? {
    style: {
      height
    }
  } : {};
  const tableClasses = dataTableBem.b({
    'sticky-header': scrollable === 'sticky-header',
    small,
    plain
  });
  const selectAllProps = hasSelectableRows && headers.selectionInputProps ? {
    isSelected: hasAllRowsSelected(),
    selectAllProps: _objectSpread2(_objectSpread2({}, headers.selectionInputProps), {}, {
      onChange: toggleSelectAll
    })
  } : {};
  const headerSortingPropsList = sortingPropsList ? sortingPropsList.map(sortingProps => _objectSpread2(_objectSpread2({}, sortingProps), {}, {
    sortingFunction: newSorting => onSortingChange(newSorting, sortingProps.comparator)
  })) : null;
  return /*#__PURE__*/React__default__default.createElement("div", _extends({
    className: dataTableControlsBem.b({}, extraClasses)
  }, rest), /*#__PURE__*/React__default__default.createElement("div", _extends({
    className: wrapperClasses
  }, wrapperStyle, {
    ref: innerRef
  }), /*#__PURE__*/React__default__default.createElement("table", {
    className: tableClasses
  }, caption && /*#__PURE__*/React__default__default.createElement("caption", null, caption), /*#__PURE__*/React__default__default.createElement(DataTableHeader, _extends({}, selectAllProps, {
    headers: headers.cells,
    sortingPropsList: headerSortingPropsList
  })), /*#__PURE__*/React__default__default.createElement("tbody", null, sortedRows.map((row, index) => {
    const rowProps = _objectSpread2({}, row);
    if (hasSelectableRows && row.selectionInputProps) {
      rowProps.isSelected = getRowSelectionStatus(row.selectionInputProps.value);
      rowProps.selectionInputProps = _objectSpread2(_objectSpread2({}, row.selectionInputProps), {}, {
        onChange: onRowChange
      });
    }
    return /*#__PURE__*/React__default__default.createElement(DataTableRow, _extends({}, rowProps, {
      key: row.key || index,
      cells: row.cells,
      selectionInputProps: rowProps.selectionInputProps
    }));
  })), footers && /*#__PURE__*/React__default__default.createElement(DataTableFooter, {
    footers: footers.cells
  }))), pager);
};
DataTable.displayName = 'DataTable';
DataTable.defaultProps = {
  caption: null,
  footers: null,
  plain: false,
  small: false,
  collapsed: false,
  scrollable: null,
  height: null,
  hasSelectableRows: false,
  onRowSelection: null,
  sortingPropsList: null,
  pager: null,
  extraClasses: null
};
DataTable.propTypes = {
  /** The table caption */
  caption: PropTypes__default.string,
  /** Table headers and optional selec-all checkbox properties */
  headers: PropTypes__default.shape({
    /** List of table headers: [`string` | `node` | `number`] */
    cells: CellContent.isRequired,
    /** { 'aria-label': `string`, id: `string`, value: `string` | `number` } */
    selectionInputProps: SelectionInputProps
  }).isRequired,
  /** List of table rows, each row is an object
   *  with one required array of `cells` and an optional `selectionInputProps`
   *  which is required if you opt for a table with selectable rows.
   */
  rows: PropTypes__default.arrayOf(PropTypes__default.shape({
    /** List of row cells: [`string` | `node` | `number`] */
    cells: CellContent.isRequired,
    /** { 'aria-label': `string`, id: `string`, value: `string` | `number` } */
    selectionInputProps: SelectionInputProps,
    /** Optional custom key to improve react performance */
    key: PropTypes__default.string
  })).isRequired,
  /** Optional table footers */
  footers: PropTypes__default.shape({
    /** List of table footers: [`string` | `node` | `number`] */
    cells: CellContent.isRequired
  }),
  /** Enables plain table variation (turns off zebra rows) */
  plain: PropTypes__default.bool,
  /** Enables smaller table variation */
  small: PropTypes__default.bool,
  /** Enables collapsed table variation */
  collapsed: PropTypes__default.bool,
  /** Enables scrollable table */
  scrollable: PropTypes__default.oneOf(['default', 'sticky-header']),
  /** Fixed height (in pixels) applied to scrollable tables */
  height: PropTypes__default.oneOfType([PropTypes__default.string, PropTypes__default.number]),
  /** Enables selectable rows */
  hasSelectableRows: PropTypes__default.bool,
  /** Called back with updated state whenever row selection changes */
  onRowSelection: PropTypes__default.func,
  /** Optional List of properties for sorting rows with a given column index */
  sortingPropsList: PropTypes__default.arrayOf(PropTypes__default.shape({
    /** The column index this sorting will take place,
     * reflects the index in headers and rows cells */
    columnIndex: PropTypes__default.number.isRequired,
    /** default aria-label for the sorting button */
    label: PropTypes__default.string.isRequired,
    /** aria-label for the sorting button when it's ascending */
    labelAsc: PropTypes__default.string.isRequired,
    /** aria-label for the sorting button when it's descending */
    labelDesc: PropTypes__default.string.isRequired,
    /** Optional function used as a sort comparator. (a, b, columnIndex) */
    comparator: PropTypes__default.func
  })),
  /** Optional Pager component for table pagination
   * @see See [Pager](/#/Components/Pager)
   */
  pager(_ref2) {
    let {
      pager
    } = _ref2;
    let error = null;
    if (pager && pager.type && !ReactUtils.isOfType(pager, 'Pager')) {
      error = new Error("DataTable only accepts Pager component as \"pager\" prop but received \"".concat(ReactUtils.getElementName(pager), "\"."));
    }
    return error;
  },
  /** React ref forwarded to the DataTable container. */
  innerRef: PropTypes__default.oneOfType([PropTypes__default.func, PropTypes__default.shape({
    current: PropTypes__default.object
  })]),
  /** Custom classes names for data table wrapper element */
  extraClasses: PropTypes__default.string
};
var dataTable = Injecss(DataTable, styles);

export { DataTable, dataTable as default };
